/**
 * DocumentRail — middle pane of the Matter AI Workspace.
 *
 * The user picks up to 3 documents that the action will operate on.
 * For Phase 1a, documents are entered manually (paste a SharePoint
 * link or use the matter's existing doc list).  Phase 1b will plug
 * into the existing matter doc surface.
 *
 * The character-count indicator is visible at all times so the user
 * knows BEFORE running whether they're inside the Phase 1a limits.
 */
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { FileText, Plus, X } from "lucide-react";

const MAX_DOCUMENTS = 3;

export default function DocumentRail({ selectedDocuments, onChange, maxCharactersPerDocument }) {
    const [draftTitle, setDraftTitle] = useState("");
    const [draftDriveId, setDraftDriveId] = useState("");
    const [draftItemId, setDraftItemId] = useState("");

    const handleAdd = () => {
        if (!draftTitle.trim() || !draftDriveId.trim() || !draftItemId.trim()) return;
        if (selectedDocuments.length >= MAX_DOCUMENTS) return;
        const newDoc = {
            document_id: `${draftDriveId.trim()}/${draftItemId.trim()}`,
            document_title: draftTitle.trim(),
            sharepoint_drive_id: draftDriveId.trim(),
            sharepoint_item_id: draftItemId.trim(),
        };
        onChange([...selectedDocuments, newDoc]);
        setDraftTitle("");
        setDraftDriveId("");
        setDraftItemId("");
    };

    const handleRemove = (documentId) => {
        onChange(selectedDocuments.filter(d => d.document_id !== documentId));
    };

    const limitReached = selectedDocuments.length >= MAX_DOCUMENTS;

    return (
        <div className="space-y-3" data-testid="aiworkspace-document-rail">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Selected documents</h3>
                <Badge
                    variant="outline"
                    className={limitReached ? "border-status-amber text-status-amber" : "border-slate-500 text-slate-400"}
                    data-testid="aiworkspace-doc-count-indicator"
                >
                    {selectedDocuments.length} / {MAX_DOCUMENTS}
                </Badge>
            </div>

            {/* Selected list */}
            {selectedDocuments.length === 0 ? (
                <div className="text-xs text-slate-500 italic py-2" data-testid="aiworkspace-no-docs-message">
                    No documents selected yet. Add up to {MAX_DOCUMENTS} below.
                </div>
            ) : (
                <ul className="space-y-1.5" data-testid="aiworkspace-doc-list">
                    {selectedDocuments.map(doc => (
                        <li
                            key={doc.document_id}
                            className="flex items-center gap-2 p-2 bg-navy-hover/40 rounded border border-navy-border"
                            data-testid={`aiworkspace-doc-${doc.document_id}`}
                        >
                            <FileText className="w-4 h-4 text-teal flex-shrink-0" />
                            <span className="flex-1 text-sm text-white truncate">{doc.document_title}</span>
                            <button
                                onClick={() => handleRemove(doc.document_id)}
                                className="text-slate-400 hover:text-status-red"
                                data-testid={`aiworkspace-doc-remove-${doc.document_id}`}
                                aria-label={`Remove ${doc.document_title}`}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {/* Add form */}
            {!limitReached && (
                <div className="pt-2 border-t border-navy-border" data-testid="aiworkspace-doc-add-form">
                    <p className="text-xs text-slate-500 mb-1.5">
                        Add a SharePoint document (title + drive ID + item ID). Phase 1a supports .txt, .md, and .pdf — other formats are skipped.
                    </p>
                    <div className="space-y-1.5">
                        <Input
                            placeholder="Document title (e.g. share-purchase-agreement-v3.pdf)"
                            value={draftTitle}
                            onChange={e => setDraftTitle(e.target.value)}
                            className="text-sm h-8"
                            data-testid="aiworkspace-doc-add-title"
                        />
                        <div className="flex gap-1.5">
                            <Input
                                placeholder="Drive ID"
                                value={draftDriveId}
                                onChange={e => setDraftDriveId(e.target.value)}
                                className="text-xs h-8"
                                data-testid="aiworkspace-doc-add-drive-id"
                            />
                            <Input
                                placeholder="Item ID"
                                value={draftItemId}
                                onChange={e => setDraftItemId(e.target.value)}
                                className="text-xs h-8"
                                data-testid="aiworkspace-doc-add-item-id"
                            />
                            <Button
                                onClick={handleAdd}
                                disabled={!draftTitle.trim() || !draftDriveId.trim() || !draftItemId.trim()}
                                size="sm"
                                className="bg-teal hover:bg-teal/90"
                                data-testid="aiworkspace-doc-add-btn"
                            >
                                <Plus className="w-3 h-3 mr-1" /> Add
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <p className="text-xs text-slate-500 italic">
                Each document is capped at {maxCharactersPerDocument.toLocaleString()} characters at fetch time.
            </p>
        </div>
    );
}
